import * as React from 'react';
import { connect } from 'react-redux';
import { debounce } from 'lodash';

import ActionButton from 'components/buttons/action-button/ActionButton';
import { FormField } from 'components/form/index';
import Input from 'components/inputs/input/Input';
import MediaPreview from 'components/MediaPreview';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import Spinner from 'components/spinner/Spinner';

import { fetchItemInfoByURLOrSKU } from 'modules/realm-outfits/reducers/realm-outfits';

import { __t, applyTranslations } from 'utils/language';
import { isRequired } from 'utils/validators';

import './realm-outfit-item-form.scss';

applyTranslations(require('./realm-outfit-item-form.i18n.json'));

import type {
  Data,
  MixedSelectDropdownItem,
  RealmOutfitItemResource,
  TextEntry
} from 'types';

type OwnProps = {
  onSubmit: (a: RealmOutfitItemResource) => void,
  title?: string,
  hasSimilarItem?: boolean
};

type DispatchProps = {
  dispatchFetchItemInfoByURLOrSKU: (a: string) => Promise<RealmOutfitItemResource>
};

type Props = OwnProps & DispatchProps;

type State = {
  item: RealmOutfitItemResource | undefined | null,
  url: string,
  type: string,
  errorMessage: string,
  isLoading: boolean
};

const REALM_OUTFIT_ITEM_TYPES = ['similar', 'exact'];

const outfitItemOptions = REALM_OUTFIT_ITEM_TYPES.map(type => ({
  label: __t(`realm_outfit_item_form.types.${type}`),
  value: type
}));

class RealmOutfitItemForm extends React.PureComponent<Props, State> {
  debouncedHandleUrlChanged: (a: string) => void;

  state: State;

  constructor(props: Props) {
    super(props);

    this.state = this.getDefaultState();
    this.debouncedHandleUrlChanged = debounce(this.onUrlChanged, 300);
  }

  getDefaultState(): State {
    return {
      item: null,
      url: '',
      type: 'exact',
      errorMessage: '',
      isLoading: false
    };
  }

  getDropdownItems = (): Array<MixedSelectDropdownItem> => {
    const { type } = this.state;
    const { hasSimilarItem } = this.props;

    return outfitItemOptions.map(item => ({
      identifier: item.value,
      isDisabled: false,
      isSelected: type === item.value,
      label: item.label,
      value: item.value
    })).filter(i => (i.identifier === 'exact' || hasSimilarItem));
  };

  handleTypeChange = (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null,
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ): void => (
    this.setState({
      type: selectedItem?.identifier || updatedItems[0].identifier
    })
  );

  onUrlChanged = (value: string) => {
    const { dispatchFetchItemInfoByURLOrSKU } = this.props;

    this.setState({
      url: value,
      isLoading: true
    });

    dispatchFetchItemInfoByURLOrSKU(value)
      .then((item) => {
        this.setState({
          item,
          errorMessage: '',
          isLoading: false
        });
      })
      .catch(() => {
        this.setState({
          errorMessage: __t('realm_outfit_item_form.invalid_article_input'),
          isLoading: false
        });
      });
  };

  handleUrlChanged = (event: React.SyntheticEvent<TextEntry>) => {
    this.debouncedHandleUrlChanged(event.currentTarget.value);
  };

  handleSubmit = () => {
    const { onSubmit } = this.props;
    const { item, type } = this.state;

    if (!item) {
      return;
    }

    onSubmit({
      ...item,
      isSimilar: type === 'similar'
    });

    this.setState({
      ...this.getDefaultState()
    });
  };

  renderPreview = () => {
    const { item } = this.state;

    if (!item) {
      return <div className="realm-outfit-item-form__placeholder" />;
    }

    const attachment = {
      id: item.id,
      url: item.imageUrl,
      assetType: 'image',
      format: 'image',
      meta: {
        type: 'highres'
      }
    };

    return (
      <MediaPreview
        className="realm-outfit-item-form__preview"
        attachment={attachment}
        width={122}
        height={122}
      />
    );
  };

  renderLoader = () => (
    <Spinner
      position="cover"
      size="small"
      color="light"
    />
  );

  render() {
    const { isLoading, errorMessage, url, type, item } = this.state;
    const { title } = this.props;
    const dropdownItems = this.getDropdownItems();

    return (
      <div className="realm-outfit-item-form">
        {
            title && (
              <div className="realm-outfit-item-form__title">
                {title}
              </div>
            )
          }
        <div className="realm-outfit-item-form__content">
          <div className="realm-outfit-item-form__preview-wrapper">
            {this.renderPreview()}
            {isLoading && this.renderLoader()}
          </div>
          <div>
            <div className="realm-outfit-item-form__select">
              <FormField
                label={__t('realm_outfit_item_form.label_input_type')}
                labelType="highlighted"
                labelSize="collapsed"
                showValidationIcon={false}
                direction="row"
                fontSize="small"
                validators={[
                  isRequired(type)
                ]}
              >
                <MixedSelectDropdown
                  key={`realm_outfit_item_form__dropdown--${type}`}
                  name="realm_outfit_item_form__dropdown"
                  items={dropdownItems}
                  placeholder={__t('realm_outfit_item_form.label_input_type')}
                  withSelectedInList
                  className="realm-outfit-item-form__dropdown"
                  onItemsToggle={this.handleTypeChange}
                />
              </FormField>
            </div>
            <div className="realm-outfit-item-form__input">
              <FormField
                direction="column"
                showValidationIcon={false}
              >
                <Input
                  onChange={this.handleUrlChanged}
                  name="url"
                  width="responsive"
                  value={url}
                  placeholder={__t('realm_outfit_item_form.article_placeholder')}
                />
                {
                    errorMessage &&
                      <span className="realm-outfit-item-form__error">
                        {errorMessage}
                      </span>
                  }
              </FormField>
            </div>
            <ActionButton
              onClick={this.handleSubmit}
              color="tertiary"
              disabled={isLoading || item?.isPreOwned || !url}
            >
              {__t('realm_outfit_item_form.button_add')}
            </ActionButton>
          </div>
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchItemInfoByURLOrSKU: (text: string) => dispatch(fetchItemInfoByURLOrSKU(text))
});

export default connect(null, mapDispatchToProps)(RealmOutfitItemForm);
